import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { supabase } from '@/services/supabase';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Badge from '@/components/common/Badge';

interface JourneyVideo {
  id: string;
  business_id: string;
  creator_user_id: string;
  video_url: string | null;
  thumbnail_url: string | null;
  duration_seconds: number | null;
  status: string;
  display_order: number;
  rejection_reason: string | null;
  reviewed_at: string | null;
  created_at: string;
  business?: { business_name: string; business_id: string };
  creator?: { full_name: string; user_id: string };
}

interface VideoStats {
  total: number;
  pending_review: number;
  approved: number;
  rejected: number;
  archived: number;
  subscriptions: number;
}

const STATUS_BADGE_MAP: Record<string, { variant: 'success' | 'warning' | 'danger' | 'info' | 'default'; label: string }> = {
  pending_upload: { variant: 'warning', label: 'Pending Upload' },
  uploaded: { variant: 'info', label: 'Uploaded' },
  pending_review: { variant: 'warning', label: 'Pending Review' },
  approved: { variant: 'success', label: 'Approved' },
  archived: { variant: 'default', label: 'Archived' },
  rejected: { variant: 'danger', label: 'Rejected' },
  deleted_by_creator: { variant: 'default', label: 'Deleted' },
};

const PAGE_SIZE = 20;

export default function JourneyVideos() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Fetch stats
  const { data: stats } = useQuery<VideoStats>({
    queryKey: ['journeyVideoStats'],
    queryFn: async () => {
      const [
        { count: total },
        { count: pending_review },
        { count: approved },
        { count: rejected },
        { count: archived },
        { count: subscriptions },
      ] = await Promise.all([
        supabase.from('journey_videos').select('*', { count: 'exact', head: true }),
        supabase.from('journey_videos').select('*', { count: 'exact', head: true }).eq('status', 'pending_review'),
        supabase.from('journey_videos').select('*', { count: 'exact', head: true }).eq('status', 'approved'),
        supabase.from('journey_videos').select('*', { count: 'exact', head: true }).eq('status', 'rejected'),
        supabase.from('journey_videos').select('*', { count: 'exact', head: true }).eq('status', 'archived'),
        supabase.from('business_video_subscriptions').select('*', { count: 'exact', head: true }).eq('status', 'active'),
      ]);

      return {
        total: total || 0,
        pending_review: pending_review || 0,
        approved: approved || 0,
        rejected: rejected || 0,
        archived: archived || 0,
        subscriptions: subscriptions || 0,
      };
    },
  });

  // Fetch videos
  const { data: videosData, isLoading } = useQuery({
    queryKey: ['journeyVideos', page, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('journey_videos')
        .select(`
          *,
          business:business_profiles!business_id(business_name, business_id),
          creator:user_profiles!creator_user_id(full_name, user_id)
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, count, error } = await query;
      if (error) throw error;
      return { videos: data || [], total: count || 0 };
    },
  });

  const columns = useMemo<ColumnDef<JourneyVideo>[]>(
    () => [
      {
        accessorKey: 'thumbnail_url',
        header: '',
        size: 60,
        cell: ({ row }) => (
          <div className="w-12 h-16 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
            {row.original.thumbnail_url ? (
              <img
                src={row.original.thumbnail_url}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'business',
        header: 'Business',
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-gray-900 text-sm">
              {row.original.business?.business_name || 'Unknown'}
            </div>
            <div className="text-xs text-gray-500 truncate max-w-[150px]">
              {row.original.business_id}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'creator',
        header: 'Creator',
        cell: ({ row }) => (
          <div className="text-sm text-gray-900">
            {row.original.creator?.full_name || 'Unknown'}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const config = STATUS_BADGE_MAP[row.original.status] || { variant: 'default' as const, label: row.original.status };
          return <Badge variant={config.variant}>{config.label}</Badge>;
        },
      },
      {
        accessorKey: 'duration_seconds',
        header: 'Duration',
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">
            {row.original.duration_seconds ? `${row.original.duration_seconds}s` : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {new Date(row.original.created_at).toLocaleDateString()}
          </span>
        ),
      },
      {
        accessorKey: 'video_url',
        header: '',
        size: 80,
        cell: ({ row }) =>
          row.original.video_url ? (
            <a
              href={row.original.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View
            </a>
          ) : null,
      },
    ],
    []
  );

  const statCards = [
    { label: 'Total Videos', value: stats?.total || 0, color: 'text-gray-900' },
    { label: 'Pending Review', value: stats?.pending_review || 0, color: 'text-amber-600' },
    { label: 'Approved (Live)', value: stats?.approved || 0, color: 'text-green-600' },
    { label: 'Active Subscriptions', value: stats?.subscriptions || 0, color: 'text-blue-600' },
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'pending_review', label: 'Pending Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'archived', label: 'Archived' },
    { value: 'pending_upload', label: 'Pending Upload' },
    { value: 'deleted_by_creator', label: 'Deleted by Creator' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Journey Videos</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage consumer video testimonials across all businesses.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.label}>
            <CardBody>
              <div className="text-sm text-gray-500">{stat.label}</div>
              <div className={`text-2xl font-bold mt-1 ${stat.color}`}>{stat.value}</div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Data Table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={videosData?.videos || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={videosData?.total}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>
    </div>
  );
}
